import { useEffect, useState } from 'react';
import type { Product } from '../data/products';

type CatalogState = {
  products: Product[];
  loading: boolean;
  error: string | null;
};

export function useCatalog(): CatalogState {
  const [state, setState] = useState<CatalogState>({
    products: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/marketplace');
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Catalog unavailable');
        if (!cancelled) {
          setState({
            products: Array.isArray(data.products) ? data.products : [],
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            products: [],
            loading: false,
            error: err instanceof Error ? err.message : 'Catalog unavailable',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function useProduct(slug: string | undefined) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    (async () => {
      try {
        const res = await fetch(`/api/marketplace/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (res.status === 404) {
          if (!cancelled) {
            setProduct(null);
            setNotFound(true);
            setLoading(false);
          }
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Unable to load product');
        if (!cancelled) {
          setProduct(data.product || null);
          setNotFound(!data.product);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load product');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { product, loading, error, notFound };
}
